import { ExternalLink, Copy, Check } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";

interface SolanaExplorerLinkProps {
  address: string;
  type?: "address" | "tx";
  label?: string;
  cluster?: "devnet" | "mainnet-beta" | "testnet";
  showFull?: boolean;
  variant?: "link" | "badge" | "button";
  className?: string;
}

export function SolanaExplorerLink({
  address,
  type = "address",
  label,
  cluster = "devnet",
  showFull = false,
  variant = "link",
  className = "",
}: SolanaExplorerLinkProps) {
  const [copied, setCopied] = useState(false);

  const explorerUrl = `https://explorer.solana.com/${type}/${address}?cluster=${cluster}`;
  const displayAddress = showFull ? address : `${address.slice(0, 4)}...${address.slice(-4)}`;

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await navigator.clipboard.writeText(address);
    setCopied(true);
    toast({ title: "Copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  };

  if (variant === "badge") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={className}
            >
              <Badge
                variant="outline"
                className="gap-1 cursor-pointer hover:bg-primary/10 transition-colors font-mono text-xs"
              >
                {label && <span className="text-muted-foreground">{label}:</span>}
                {displayAddress}
                <ExternalLink className="w-3 h-3 ml-1" />
              </Badge>
            </a>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="flex items-center gap-2">
            <span className="font-mono text-xs">{address}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (variant === "button") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {label && <span className="text-sm text-muted-foreground">{label}:</span>}
        <Button
          variant="outline"
          size="sm"
          className="gap-2 font-mono text-xs"
          onClick={handleCopy}
        >
          {displayAddress}
          {copied ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ExternalLink className="h-4 w-4" />
          </Button>
        </a>
      </div>
    );
  }

  // Default: link variant
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 text-primary hover:underline font-mono text-sm ${className}`}
          >
            {label && <span className="text-muted-foreground">{label}:</span>}
            {displayAddress}
            <ExternalLink className="w-3 h-3" />
          </a>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="flex items-center gap-2">
          <span className="font-mono text-xs">{address}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={handleCopy}
          >
            {copied ? (
              <Check className="h-3 w-3 text-green-500" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Component for displaying Program ID prominently
export function ProgramIdDisplay({ className = "" }: { className?: string }) {
  const PROGRAM_ID = "FcR1r39QK7emuG3Yp2YV9Y3Rv7XjaB5M6B8RCeUBJSn5";

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">
        Smart Contract Program ID
      </div>
      <SolanaExplorerLink
        address={PROGRAM_ID}
        variant="button"
        cluster="devnet"
      />
    </div>
  );
}

// Component for displaying NFT on-chain info
interface NftOnChainInfoProps {
  nftAccountPubkey?: string;
  ownerPubkey?: string;
  priceEmon?: number;
  priceUsd?: number;
  salesCount?: number;
  className?: string;
}

export function NftOnChainInfo({
  nftAccountPubkey,
  ownerPubkey,
  priceEmon,
  priceUsd,
  salesCount,
  className = "",
}: NftOnChainInfoProps) {
  if (!nftAccountPubkey) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        Not yet initialized on-chain
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <SolanaExplorerLink
          address={nftAccountPubkey}
          label="NFT Account"
          variant="badge"
          cluster="devnet"
        />
        {ownerPubkey && (
          <SolanaExplorerLink
            address={ownerPubkey}
            label="Owner"
            variant="badge"
            cluster="devnet"
          />
        )}
      </div>
      {(priceEmon !== undefined || salesCount !== undefined) && (
        <div className="flex items-center gap-4 text-sm">
          {priceEmon !== undefined && (
            <span>
              <span className="text-muted-foreground">Price:</span>{" "}
              <span className="font-semibold text-primary">
                {priceEmon.toLocaleString()} EMON
              </span>
              {priceUsd !== undefined && (
                <span className="text-muted-foreground ml-1">
                  (≈ ${priceUsd.toFixed(2)} USD)
                </span>
              )}
            </span>
          )}
          {salesCount !== undefined && (
            <span>
              <span className="text-muted-foreground">Sales:</span>{" "}
              <span className="font-semibold">{salesCount}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
